<template>
  <apex-chart-card
    chart-title="Histórico de cashback"
    chart-description="Histórico do cashback adquirido e consumido pelos consumidores e previsibilidade da expiração de cashback nos próximos meses."
    :filter-by-date-period.sync="filterByDatePeriod"
    :config-update-options="configUpdateOptions"
    :chart-loading="$apollo.queries[tab ? 'futureCashbackExpirationsMetric' : 'timelineCashbackMetrics'].loading || skipQuery"
    :chart-data="tab ? futureCashbackExpirationsMetric : timelineCashbackMetrics"
    :series-data="seriesData"
    :data-for-export-csv="dataForExportCsv"
    :chartColors="tab ? ['#E03210'] : ['#002040', '#00B395', '#E03210']"
    loader="cashback-metrics-chart"
    use-export-file
  >
    <template v-slot:tabs-menu>
      <mf-tabs-menu key="chackback-history-chart-tabs" v-model="tab" :tabs="tabItems" class="my-4" />
    </template>
  </apex-chart-card>
</template>

<script>
import { TIMELINE_EXPIRED_CASHBACK_METRICS, TIMELINE_FUTURE_CASHBACK_METRICS } from '@/modules/mobile/cashback/graphql'
import { colors } from '@/config/colors.js'
import { DateTime } from 'luxon'
import { assertArray } from '@/config/helpers.js'
import { mapGetters } from 'vuex'
import ApexChartCard from '@/components/ApexChartCard.vue'

export default {
  name: 'CashbackMetricsChart',
  components: { ApexChartCard },
  props: {
    stateFilters: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    tab: 0,
    filterByDatePeriod: 'mensal',
    tabItems: [
      {
        name: 'Histórico',
        itemsLength: 0,
        haveLength: false,
      },
      {
        name: 'Futuro',
        itemsLength: 0,
        haveLength: false,
      },
    ],
  }),
  computed: {
    ...mapGetters(['units']),
    queryGroupFilterByDatePeriod() {
      switch (this.filterByDatePeriod) {
        case 'semanal':
          return 'WEEK'
        case 'diario':
          return 'DAY'
        default:
          return 'MONTH'
      }
    },

    configUpdateOptions() {
      return {
        tooltip: {
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            if (this.tab) {
              const expiredCashbackMetric = this.formatCurrency(series[0][dataPointIndex])
              const expiredCashbackMetricComponent = expiredCashbackMetric
                ? `
                  <p class="ma-0">
                    <span style="color: #E03210; font-weight: bold">${expiredCashbackMetric}</span>
                    <span class="cinza-d10-text"> de cashback expirado</span>
                  </p>`
                : ''

              return `
                    <v-card class="px-3 py-2">
                      <v-card-text>
                        ${expiredCashbackMetricComponent}
                      </v-card-text>
                    </v-card>
                  `
            } else {
              const acquiredCashbackMetric = this.formatCurrency(series[0][dataPointIndex])
              const consumedCashbackMetric = this.formatCurrency(series[1][dataPointIndex])
              const expiredCashbackMetric = this.formatCurrency(series[2][dataPointIndex])

              const acquiredCashbackMetricComponent = acquiredCashbackMetric
                ? `
                  <p class="ma-0">
                    <span style="color: ${colors[0]}; font-weight: bold">${acquiredCashbackMetric}</span>
                    <span class="cinza-d10-text"> de cashback adquirido</span>
                  </p>`
                : ''

              const consumedCashbackMetricComponent = consumedCashbackMetric
                ? `
                  <p class="ma-0">
                    <span style="color: ${colors[1]}; font-weight: bold">${consumedCashbackMetric}</span>
                    <span class="cinza-d10-text"> de cashback consumido</span>
                  </p>`
                : ''
              const expiredCashbackMetricComponent = expiredCashbackMetric
                ? `
                  <p class="ma-0">
                    <span style="color: #E03210; font-weight: bold">${expiredCashbackMetric}</span>
                    <span class="cinza-d10-text"> de cashback expirado</span>
                  </p>`
                : ''

              return `
                    <v-card class="px-3 py-2">
                      <v-card-text>
                        ${acquiredCashbackMetricComponent}
                        ${consumedCashbackMetricComponent}
                        ${expiredCashbackMetricComponent}
                      </v-card-text>
                    </v-card>
                  `
            }
          },
        },
      }
    },

    seriesData() {
      if (this.tab) {
        return [
          {
            name: 'Cashback expirado',
            chartDataPropertyName: 'futureCashbackExpirationsMetric',
          },
        ]
      } else {
        return [
          {
            name: 'Cashback adquirido',
            chartDataPropertyName: 'acquiredCashbackMetric',
          },
          {
            name: 'Cashback consumido',
            chartDataPropertyName: 'consumedCashbackMetric',
          },
          {
            name: 'Cashback expirado',
            chartDataPropertyName: 'expiredCashbackMetric',
          },
        ]
      }
    },

    dataForExportCsv() {
      let headers = []
      let data = []
      let filename = 'Historico metricas cashback'

      if (this.queryGroupFilterByDatePeriod === 'WEEK') {
        headers = ['Cashback expirado', 'Data início', 'Data final']
        if (!this.tab) headers.splice(0, 0, 'Cashback adquirido', 'Cashback consumido')

        data = this.tab
          ? assertArray(this.futureCashbackExpirationsMetric).map((cashback) => [
              this.formatCurrency(cashback.futureCashbackExpirationsMetric)?.replaceAll(',', '.'),
              cashback.label[0],
              cashback.label[1],
            ])
          : assertArray(this.timelineCashbackMetrics).map((cashback) => [
              this.formatCurrency(cashback.acquiredCashbackMetric)?.replaceAll(',', '.'),
              this.formatCurrency(cashback.consumedCashbackMetric)?.replaceAll(',', '.'),
              this.formatCurrency(cashback.expiredCashbackMetric)?.replaceAll(',', '.'),
              cashback.label[0],
              cashback.label[1],
            ])
      } else {
        data = this.tab
          ? assertArray(this.futureCashbackExpirationsMetric).map((cashback) => [
              this.formatCurrency(cashback.futureCashbackExpirationsMetric)?.replaceAll(',', '.'),
              cashback.label,
            ])
          : assertArray(this.timelineCashbackMetrics).map((cashback) => [
              this.formatCurrency(cashback.acquiredCashbackMetric)?.replaceAll(',', '.'),
              this.formatCurrency(cashback.consumedCashbackMetric)?.replaceAll(',', '.'),
              this.formatCurrency(cashback.expiredCashbackMetric)?.replaceAll(',', '.'),
              cashback.label,
            ])

        headers = ['Cashback expirado', 'Data']
        if (!this.tab) headers.splice(0, 0, 'Cashback adquirido', 'Cashback consumido')
      }

      return {
        headers,
        data,
        filename,
      }
    },

    skipQuery() {
      const { data, units } = this.stateFilters
      const [startDate, endDate] = assertArray(data)
      return !units?.length || !startDate || !endDate
    },

    query() {
      return this.tab ? TIMELINE_FUTURE_CASHBACK_METRICS : TIMELINE_EXPIRED_CASHBACK_METRICS
    },

    activeUnits() {
      if (!this.units?.length) return []
      return this.units.filter((unit) => unit.active)
    },
  },
  apollo: {
    timelineCashbackMetrics: {
      query: TIMELINE_EXPIRED_CASHBACK_METRICS,
      variables() {
        const { data, units, products } = this.stateFilters
        const [startDate, endDate] = assertArray(data)
        const filters = []
        const selectedUnits = this.activeUnits.length === units?.length ? [] : units

        if (startDate) filters.push({ type: 'DATE', value: startDate, predicate: 'GREATER_THAN_OR_EQUALS' })
        if (endDate) filters.push({ type: 'DATE', value: endDate, predicate: 'LESS_THAN_OR_EQUALS' })
        if (assertArray(units).length) filters.push({ type: 'STORE', value: selectedUnits, predicate: 'IN' })
        if (assertArray(products).length) filters.push({ type: 'PRODUCT', value: products, predicate: 'IN' })

        return {
          groupBy: this.queryGroupFilterByDatePeriod,
          filters,
        }
      },
      context() {
        return {
          uri: this.$microservicesUrls['cashback'],
        }
      },
      update({ timelineCashbackMetrics }) {
        return timelineCashbackMetrics?.data
      },
      skip() {
        return this.skipQuery
      },
    },
    futureCashbackExpirationsMetric: {
      query: TIMELINE_FUTURE_CASHBACK_METRICS,
      variables() {
        const { unidades, products } = this.stateFilters
        const filters = []

        const startDate = DateTime.now().toFormat('yyyy-MM-dd')
        const endDate =
          this.queryGroupFilterByDatePeriod === 'DAY'
            ? DateTime.now().plus({ days: 30 }).toFormat('yyyy-MM-dd')
            : this.queryGroupFilterByDatePeriod === 'WEEK'
            ? DateTime.now().plus({ months: 3 }).toFormat('yyyy-MM-dd')
            : DateTime.now().plus({ years: 1 }).toFormat('yyyy-MM-dd')

        if (startDate) filters.push({ type: 'DATE', value: startDate, predicate: 'GREATER_THAN_OR_EQUALS' })
        if (endDate) filters.push({ type: 'DATE', value: endDate, predicate: 'LESS_THAN_OR_EQUALS' })
        if (assertArray(unidades).length) filters.push({ type: 'STORE', value: unidades, predicate: 'IN' })
        if (assertArray(products).length) filters.push({ type: 'PRODUCT', value: products, predicate: 'IN' })

        return {
          groupBy: this.queryGroupFilterByDatePeriod,
          filters,
        }
      },
      context() {
        return {
          uri: this.$microservicesUrls['cashback'],
        }
      },
      update({ futureCashbackExpirationsMetric }) {
        return futureCashbackExpirationsMetric?.data
      },
      skip() {
        return this.skipQuery
      },
    },
  },

  methods: {
    formatCurrency(currency) {
      if (currency !== undefined && currency !== null) {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(currency)
      }
      return currency
    },
  },
}
</script>
<style lang="scss" scoped>
::v-deep .cinza-d10-text {
  color: $cinza-d10;
  font-weight: bold;
}
</style>
